import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  TextField,
  TablePagination,
  Snackbar,
  Alert,
  LinearProgress,
  Drawer,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import { CloudUpload, Close, Error as ErrorIcon, Warning as WarningIcon, Update, AddCircle } from '@mui/icons-material';
import MedicamentForm from './MedicamentForm';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../components/ConfirmationDialog';

const MedicamentList = () => {
  const [medicaments, setMedicaments] = useState([]);
  const [filteredMedicaments, setFilteredMedicaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [medicamentToDelete, setMedicamentToDelete] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [medicamentToDuplicate, setMedicamentToDuplicate] = useState(null);
  const [open, setOpen] = useState(false);
  const [currentMedicament, setCurrentMedicament] = useState(null);
  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem('medicamentsSearchQuery') || '';
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [resultsDrawer, setResultsDrawer] = useState({ open: false, data: null });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchMedicaments();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredMedicaments(medicaments);
      return;
    }

    const filtered = medicaments.filter((med) =>
      (med.dci && med.dci.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (med.specialite && med.specialite.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (med.forme && med.forme.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    setFilteredMedicaments(filtered);
  }, [searchQuery, medicaments]);

  const fetchMedicaments = async () => {
    try {
      const response = await axios.get("get_all_medicines", {
        headers: { 
          "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
          Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem("token")}`,
        },
      });
      setMedicaments(response.data);
      setFilteredMedicaments(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setCurrentMedicament(null);
    setOpen(true);
  };

  const handleUpdateClick = (medicament) => {
    setCurrentMedicament(medicament);
    setOpen(true);
  };

  const handleDeleteClick = (id) => {
    setMedicamentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteDialogOpen(false);
  
    if (!medicamentToDelete) return;
  
    setMedicaments((prev) => prev.filter((med) => med._id !== medicamentToDelete));
    setFilteredMedicaments((prev) => prev.filter((med) => med._id !== medicamentToDelete));
  
    try {
      await axios.delete(`medicament/${medicamentToDelete}`, {
        headers: {
          "x-access-token":
            localStorage.getItem("token") || sessionStorage.getItem("token"),
          Authorization: `Bearer ${
            localStorage.getItem("token") || sessionStorage.getItem("token")
          }`,
        },
      });
    } catch (error) {
      console.error("Error deleting medicament:", error);
      alert("Failed to delete medicament. Please try again.");
      fetchMedicaments();
    }
  };

  const handleVariantsClick = (medicamentId, specialite) => {
    navigate(`/medicaments/${medicamentId}/${specialite}/variants`);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentMedicament(null);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    sessionStorage.setItem('medicamentsSearchQuery', query);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({...snackbar, open: false});
  };

  const handleDuplicateClick = (id) => {
    setMedicamentToDuplicate(id);
    setDuplicateDialogOpen(true);
  };

  const confirmDuplicate = async () => {
    setDuplicateDialogOpen(false);
  
    if (!medicamentToDuplicate) return;
  
    try {
      setLoading(true);
      const response = await axios.post(
        `medicaments/${medicamentToDuplicate}/duplicate`,
        {},
        {
          headers: { 
            "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
            Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem('token')}`,
          },
        }
      );
      
      fetchMedicaments();
      
      setSnackbar({ 
        open: true, 
        message: `Medicament duplicated successfully as ${response.data.medicament.specialite}`,
        severity: 'success'
      });
    } catch (error) {
      console.error("Error duplicating medicament:", error);
      setSnackbar({ 
        open: true, 
        message: "Failed to duplicate medicament. Please try again.",
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportMedicamentsClick = () => {
    fileInputRef.current.click();
  };

  const handleMedicamentsFileChange = async (event) => {
    const file = event.target.files[0];
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];
  
    if (!file) return;
  
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please upload a .xls, .xlsx, or .csv file.');
      event.target.value = ''; // Reset file input value
      return;
    }
  
    if (file.size === 0) {
      alert('Cannot upload empty files. Please select a file with content.');
      event.target.value = '';
      return;
    }
  
    if (file.size > 10 * 1024 * 1024) { // 10MB
      alert('File size must be less than 10MB.');
      event.target.value = ''; 
      return;
    }
  
    try {
      const formData = new FormData();
      formData.append('file', file);

      setUploading(true);
      setUploadProgress(0);
  
      const response = await axios.post(
        'admin/medicaments/add/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            "x-access-token": localStorage.getItem("token") || sessionStorage.getItem('token'),
            Authorization: `Bearer ${localStorage.getItem("token") || sessionStorage.getItem('token')}`,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        },
      );

      setResultsDrawer({ open: true, data: response.data });
  
      setSnackbar({
        open: true,
        message: 'File uploaded successfully!',
        severity: 'success',
      });
      
    } catch (err) {
      console.error(err);

      setResultsDrawer({ open: true, data: { error: err.response?.data?.error || 'An error occurred while uploading the file.' } });

      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'An error occurred while uploading the file.',
        severity: 'error',
      });
    } finally {
      setUploading(false);
      fetchMedicaments();
      event.target.value = ''; 
    }
  };

  // Expandable chip logic for upload results drawer
  const UploadResultsDrawerContent = ({ data }) => {
  const [expandedIdx, setExpandedIdx] = useState({});
  const [allExpanded, setAllExpanded] = useState(false);

    if (!data) return null;

    // Helper to collect all keys for expand/collapse all
    const getAllKeys = () => {
      const keys = [];
      if (data.medsUpsertingOp) {
        if (data.medsUpsertingOp.succeeded)
          data.medsUpsertingOp.succeeded.forEach((_, idx) => keys.push('medsUpsertingOp-succeeded-' + idx));
        if (data.medsUpsertingOp.failed)
          data.medsUpsertingOp.failed.forEach((_, idx) => keys.push('medsUpsertingOp-failed-' + idx));
      }
      if (data.variantUpsertingOp) {
        if (data.variantUpsertingOp.succeeded)
          data.variantUpsertingOp.succeeded.forEach((_, idx) => keys.push('variantUpsertingOp-succeeded-' + idx));
        if (data.variantUpsertingOp.failed)
          data.variantUpsertingOp.failed.forEach((_, idx) => keys.push('variantUpsertingOp-failed-' + idx));
      }
      if (data.matchingVariantsWithMedsAndPathologies) {
        if (data.matchingVariantsWithMedsAndPathologies.failedMatchings)
          data.matchingVariantsWithMedsAndPathologies.failedMatchings.forEach((_, idx) => keys.push('matchingVariantsWithMedsAndPathologies-failed-' + idx));
      }
      return keys;
    };

    const handleExpand = (section, idx) => {
      setExpandedIdx((prev) => ({
        ...prev,
        [section + '-' + idx]: !prev[section + '-' + idx],
      }));
    };

    const handleToggleAll = () => {
      if (allExpanded) {
        setExpandedIdx({});
        setAllExpanded(false);
      } else {
        const allKeys = getAllKeys();
        const expanded = {};
        allKeys.forEach((k) => { expanded[k] = true; });
        setExpandedIdx(expanded);
        setAllExpanded(true);
      }
    };

    const renderMedicamentOps = (op) => (
      <Box>
        {op?.succeeded?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle1" color="success.main" fontWeight={600} mb={1}>
              Medicaments Upserted ({op.succeeded.length})
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {op.succeeded.map((item, idx) => (
                <li key={idx}>
                  <Chip
                    icon={item.action === 'inserted' ? <AddCircle color="success" /> : <Update color="info" />}
                    label={
                      <span>
                        <b>{item.medicament.specialite}</b>
                        <span style={{ marginLeft: 8, color: item.action === 'inserted' ? '#388e3c' : '#1976d2' }}>
                          {item.action.charAt(0).toUpperCase() + item.action.slice(1)}
                        </span>
                      </span>
                    }
                    sx={{ 
                      mr: 1, 
                      mb: 1, 
                      fontWeight: 500, 
                      cursor: 'pointer',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        display: 'block',
                        textOverflow: 'unset',
                        overflow: 'visible'
                      }
                    }}
                    color={item.action === 'inserted' ? 'success' : 'info'}
                    variant="outlined"
                    onClick={() => handleExpand('medsUpsertingOp-succeeded', idx)}
                  />
                  {expandedIdx['medsUpsertingOp-succeeded-' + idx] && (
                    <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1, mt: 1, fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <pre style={{ margin: 0 }}>{JSON.stringify(item, null, 2)}</pre>
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        )}
        {op?.warning && (
          <>
            <Typography variant="subtitle1" color="warning.main" fontWeight={600} mb={1}>
              Warnings
            </Typography>
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              {op.warning}
            </Alert>
          </>
        )}
        {op?.failed?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle1" color="error.main" fontWeight={600} mb={1}>
              Medicaments Failed ({op.failed.length})
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {op.failed.map((item, idx) => (
                <li key={idx}>
                  <Chip
                    icon={<ErrorIcon color="error" />}
                    label={
                      <span>
                        <b>{item.medicament?.specialite || 'Unknown'}</b>
                        {item.medicament?.barcode && <> &mdash; <span style={{ color: '#888' }}>{item.medicament.barcode}</span></>}
                        <span style={{ marginLeft: 8, color: '#d32f2f' }}>
                          Failed: {item.cause}
                        </span>
                      </span>
                    }
                    color="error"
                    variant="outlined"
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        display: 'block',
                        textOverflow: 'unset',
                        overflow: 'visible'
                      }
                    }}
                    onClick={() => handleExpand('medsUpsertingOp-failed', idx)}
                  />
                  {expandedIdx['medsUpsertingOp-failed-' + idx] && (
                    <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1, mt: 1, fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <pre style={{ margin: 0 }}>{JSON.stringify(item, null, 2)}</pre>
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );

    const renderVariantOps = (op) => (
      <Box>
        {op?.succeeded?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle1" color="success.main" fontWeight={600} mb={1}>
              Variants Upserted ({op.succeeded.length})
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {op.succeeded.map((item, idx) => (
                <li key={idx}>
                  <Chip
                    icon={item.action === 'inserted' ? <AddCircle color="success" /> : <Update color="info" />}
                    label={
                      <span>
                        Row <b>{item.variant.rowNumberInFile}</b>
                        <span style={{ marginLeft: 8, color: item.action === 'inserted' ? '#388e3c' : '#1976d2' }}>
                          {item.action.charAt(0).toUpperCase() + item.action.slice(1)}
                        </span>
                      </span>
                    }
                    sx={{ 
                      mr: 1, 
                      mb: 1, 
                      fontWeight: 500, 
                      cursor: 'pointer',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        display: 'block',
                        textOverflow: 'unset',
                        overflow: 'visible'
                      }
                    }}
                    color={item.action === 'inserted' ? 'success' : 'info'}
                    variant="outlined"
                    onClick={() => handleExpand('variantUpsertingOp-succeeded', idx)}
                  />
                  {expandedIdx['variantUpsertingOp-succeeded-' + idx] && (
                    <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1, mt: 1, fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <pre style={{ margin: 0 }}>{JSON.stringify(item, null, 2)}</pre>
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        )}
        {op?.failed?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle1" color="error.main" fontWeight={600} mb={1}>
              Variants Failed ({op.failed.length})
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {op.failed.map((item, idx) => (
                <li key={idx}>
                  <Chip
                    icon={<ErrorIcon color="error" />}
                    label={
                      <span>
                        Row <b>{item.variant?.rowNumberInFile || '?'}</b>
                        <span style={{ marginLeft: 8, color: '#d32f2f' }}>
                          Failed
                        </span>
                      </span>
                    }
                    color="error"
                    variant="outlined"
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        display: 'block',
                        textOverflow: 'unset',
                        overflow: 'visible'
                      }
                    }}
                    onClick={() => handleExpand('variantUpsertingOp-failed', idx)}
                  />
                  {expandedIdx['variantUpsertingOp-failed-' + idx] && (
                    <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1, mt: 1, fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <pre style={{ margin: 0 }}>{JSON.stringify(item, null, 2)}</pre>
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );

    const renderMatchingVariants = (matching) => (
      <Box>
        {matching?.failedMatchings?.length > 0 && (
          <Box mb={2}>
            <Typography variant="subtitle1" color="warning.main" fontWeight={600} mb={1}>
              Failed variant mappings ({matching.failedMatchings.length})
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              {matching.failedMatchings.map((item, idx) => (
                <li key={idx}>
                  <Chip
                    icon={<WarningIcon color="warning" />}
                    label={
                      <span>
                        Row <b>{item.rowNumberInFile}</b>
                        <span style={{ marginLeft: 8, color: '#ed6c02' }}>
                          {item.cause}
                        </span>
                      </span>
                    }
                    color="warning"
                    variant="outlined"
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      maxWidth: '100%',
                      height: 'auto',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        display: 'block',
                        textOverflow: 'unset',
                        overflow: 'visible'
                      }
                    }}
                    onClick={() => handleExpand('matchingVariantsWithMedsAndPathologies-failed', idx)}
                  />
                  {expandedIdx['matchingVariantsWithMedsAndPathologies-failed-' + idx] && (
                    <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1, mt: 1, fontSize: 13, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <pre style={{ margin: 0 }}>{JSON.stringify(item, null, 2)}</pre>
                    </Box>
                  )}
                </li>
              ))}
            </Box>
          </Box>
        )}
        <Box>
          Variant mapping summary: <b>{
            (matching?.succeededCount || 0)
          }</b> succeeded, <b>{
            (matching?.failedMatchings?.length || 0)
          }</b> failed
        </Box>
      </Box>
    );

    if (data.error) {
      return <Alert severity="error">{data.error}</Alert>;
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleToggleAll}>
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        </Box>
        {data.message && (
          <Typography variant="h6" gutterBottom>
            {data.message}
          </Typography>
        )}
        <Divider sx={{ mb: 2 }} />
        {/* Medicament upserting */}
        {data.medsUpsertingOp && (
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              STEP1: Medicament Upserting ({data.medsUpsertingOp.succeeded?.length + data.medsUpsertingOp.failed?.length} in total)
            </Typography>
            {renderMedicamentOps(data.medsUpsertingOp)}
          </Box>
        )}
        {/* Variant-medicament-pathology matching */}
        {data.matchingVariantsWithMedsAndPathologies && (
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
            STEP2: Mapping Variants to Medicaments and Pathologies
            </Typography>
            {renderMatchingVariants(data.matchingVariantsWithMedsAndPathologies)}
          </Box>
        )}
        {/* Variant upserting */}
        {data.variantUpsertingOp && (
          <Box mb={2}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              STEP3: Variant Upserting
            </Typography>
            {renderVariantOps(data.variantUpsertingOp)}
          </Box>
        )}
        {/* Fallback for unknown fields */}
        {Object.keys(data).filter(
          k => !['message', 'medsUpsertingOp', 'matchingVariantsWithMedsAndPathologies', 'variantUpsertingOp', 'error'].includes(k)
        ).length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Raw Response
            </Typography>
            <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1, fontSize: 13 }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(data, null, 2)}
              </pre>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box padding={2}>
      <Typography variant={isMobile ? "h4" : "h3"} gutterBottom>
        Medicaments
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {!uploading && (
          <Button variant="contained" color="primary" onClick={handleAddClick}>
            Add Medicament
          </Button>
        )}
        <Tooltip
          title={
            <>
              Import medicaments from a file.<br />
              Supported formats: .xls, .xlsx, .csv.<br />
              File size must be less than 10MB.
            </>
          }
          placement="top"
        >
        {!uploading && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<CloudUpload />}
            onClick={handleImportMedicamentsClick}
          >
            Import Medicaments
          </Button>
        )}
      </Tooltip>
      <input
        type="file"
        accept=".xls,.xlsx,.csv"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleMedicamentsFileChange}
      />
      {uploading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
            {uploadProgress}%
          </Typography>
        </Box>
      )}
      </Box>
      <TextField
        label="Search"
        variant="outlined"
        fullWidth
        margin="normal"
        value={searchQuery}
        onChange={handleSearchChange}
      />
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
              <TableRow>
                <TableCell sx={{ maxWidth: isMobile ? '100px' : '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  DCI
                </TableCell>
                <TableCell>Specialite</TableCell>
                <TableCell>Forme</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMedicaments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((med) => (
                  <TableRow key={med._id}>
                    <TableCell sx={{ maxWidth: isMobile ? '100px' : '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={med.dci}>
                        <span>{med.dci}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word', padding: isMobile ? '6px' : 'default' }}>
                      {med.specialite}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'normal', wordWrap: 'break-word', padding: isMobile ? '6px' : 'default' }}>
                      {med.forme}
                    </TableCell>
                    <TableCell align="right" sx={{ padding: isMobile ? '6px' : 'default' }}>
                      <ButtonGroup variant="outlined" size="small">
                        <Button onClick={() => handleUpdateClick(med)} color="primary">
                          Update
                        </Button>
                        <Button 
                          onClick={() => handleDuplicateClick(med._id)} 
                          color="success"
                        >
                          Duplicate
                        </Button>
                        <Button onClick={() => handleVariantsClick(med._id, med.specialite)} color="secondary">
                          View Variants
                        </Button>
                        <Button onClick={() => handleDeleteClick(med._id)} color="error">
                          Delete
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredMedicaments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </TableContainer>
      )}

      {/* Delete medicament Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Deletion"
        message="Are you sure you want to delete this medicament?"
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Duplicate medicament Confirmation Dialog */}
      <ConfirmationDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onConfirm={confirmDuplicate}
        title="Confirm Duplication"
        message="Are you sure you want to duplicate this medicament?"
        confirmText="Duplicate"
        confirmColor="info"
      />

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{currentMedicament ? 'Update Medicament' : 'Add Medicament'}</DialogTitle>
        <DialogContent>
          <MedicamentForm
            medicament={currentMedicament}
            onClose={handleClose}
            onSave={fetchMedicaments}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        anchor="right"
        open={resultsDrawer.open}
        onClose={() => setResultsDrawer((prev) => ({ ...prev, open: false }))}
      >
        <Box sx={{ width: 400, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Upload Results</Typography>
            <IconButton onClick={() => setResultsDrawer((prev) => ({ ...prev, open: false }))}>
              <Close />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', backgroundColor: '#f9f9f9', p: 2, borderRadius: 2 }}>
            <UploadResultsDrawerContent data={resultsDrawer.data} />
          </Box>
        </Box>
      </Drawer>
      {/* Button to reopen drawer if there are saved results and drawer is closed */}
      {resultsDrawer.data && !resultsDrawer.open && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}>
          <Button
            variant="contained"
            color="info"
            onClick={() => setResultsDrawer((prev) => ({ ...prev, open: true }))}
          >
            Reopen Upload Results
          </Button>
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ marginTop: '64px' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicamentList;